var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { createElement as _createElement } from "react";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/* eslint-disable react-hooks/exhaustive-deps */
import { Accordion, AccordionButton, AccordionItem, AccordionPanel, Badge, Box, Icon, } from '@chakra-ui/react';
import { observer, RecursionField, useField, useFieldSchema } from '@formily/react';
import { toArr } from '@formily/shared';
import { Fragment, useEffect, useState } from 'react';
import { RiFolderLine } from 'react-icons/ri';
import ArrayBase from '../ArrayBase';
const isAdditionComponent = (schema) => {
    var _a, _b;
    return ((_b = (_a = schema['x-component']) === null || _a === void 0 ? void 0 : _a.indexOf) === null || _b === void 0 ? void 0 : _b.call(_a, 'Addition')) > -1;
};
const isIndexComponent = (schema) => {
    var _a, _b;
    return ((_b = (_a = schema['x-component']) === null || _a === void 0 ? void 0 : _a.indexOf) === null || _b === void 0 ? void 0 : _b.call(_a, 'Index')) > -1;
};
const isRemoveComponent = (schema) => {
    var _a, _b;
    return ((_b = (_a = schema['x-component']) === null || _a === void 0 ? void 0 : _a.indexOf) === null || _b === void 0 ? void 0 : _b.call(_a, 'Remove')) > -1;
};
const isMoveUpComponent = (schema) => {
    var _a, _b;
    return ((_b = (_a = schema['x-component']) === null || _a === void 0 ? void 0 : _a.indexOf) === null || _b === void 0 ? void 0 : _b.call(_a, 'MoveUp')) > -1;
};
const isMoveDownComponent = (schema) => {
    var _a, _b;
    return ((_b = (_a = schema['x-component']) === null || _a === void 0 ? void 0 : _a.indexOf) === null || _b === void 0 ? void 0 : _b.call(_a, 'MoveDown')) > -1;
};
const isOperationComponent = (schema) => {
    return (isAdditionComponent(schema) || isRemoveComponent(schema) || isMoveDownComponent(schema) || isMoveUpComponent(schema));
};
const range = (count) => Array.from({ length: count }).map((_, i) => i);
const takeDefaultActiveKeys = (dataSourceLength, defaultOpenPanelCount) => {
    return dataSourceLength < defaultOpenPanelCount ? range(dataSourceLength) : range(defaultOpenPanelCount);
};
const insertActiveKeys = (activeKeys, index) => {
    if (activeKeys.length <= index)
        return activeKeys.concat(index);
    return activeKeys.reduce((buf, key) => {
        if (key < index)
            return buf.concat(key);
        if (key === index)
            return buf.concat([key, key + 1]);
        return buf.concat(key + 1);
    }, []);
};
const defaultProps = {
    defaultOpenPanelCount: 5,
};
export const ArrayAccordion = observer((props) => {
    const { defaultOpenPanelCount = defaultProps.defaultOpenPanelCount, size } = props, extraAccordionProps = __rest(props, ["defaultOpenPanelCount", "size"]);
    const field = useField();
    const dataSource = Array.isArray(field.value) ? field.value : [];
    const [activeKeys, setActiveKeys] = useState(takeDefaultActiveKeys(dataSource.length, defaultOpenPanelCount));
    const schema = useFieldSchema();
    useEffect(() => {
        if (!field.modified && dataSource.length) {
            setActiveKeys(takeDefaultActiveKeys(dataSource.length, defaultOpenPanelCount));
        }
    }, [dataSource.length, field]);
    if (!schema)
        throw new Error('can not found schema object');
    const renderAddition = () => {
        return schema.reduceProperties((addition, schema, key) => {
            if (isAdditionComponent(schema)) {
                return _jsx(RecursionField, { schema: schema, name: key });
            }
            return addition;
        }, null);
    };
    const renderEmpty = () => {
        if (dataSource.length)
            return;
        return (_jsxs(Box, Object.assign({ textAlign: "center", py: "9", background: "gray.50", borderRadius: "lg", color: "gray.500" }, { children: [_jsx(Icon, { as: RiFolderLine, display: "inline-block", fontSize: "3xl", mt: "1.5", mb: "-1.5" }), _jsx(Box, Object.assign({ fontSize: "md", fontWeight: "bold" }, { children: "Empty" }))] })));
    };
    const renderItems = () => {
        return (_jsx(Accordion, Object.assign({ allowMultiple: true, size: size }, extraAccordionProps, { defaultIndex: activeKeys, onChange: (keys) => setActiveKeys(toArr(keys)) }, { children: dataSource.map((item, index) => {
                const items = Array.isArray(schema.items) ? schema.items[index] || schema.items[0] : schema.items;
                const itemProps = field.query(`${field.address}.${index}`).get('componentProps');
                const props = items === null || items === void 0 ? void 0 : items['x-component-props'];
                const _a = Object.assign(Object.assign({}, props), itemProps), { header, panelStyle = {} } = _a, extraItemProps = __rest(_a, ["header", "panelStyle"]);
                const renderHeader = () => {
                    const headerText = `${header || field.title}`;
                    const path = field.address.concat(index);
                    const errors = field.form.queryFeedbacks({
                        type: 'error',
                        address: `${path}.**`,
                    });
                    return (_jsxs(ArrayBase.Item, Object.assign({ index: index, size: size, record: () => { var _a; return (_a = field.value) === null || _a === void 0 ? void 0 : _a[index]; } }, { children: [_jsx(RecursionField, { schema: items, name: index, filterProperties: (schema) => {
                                    if (!isIndexComponent(schema))
                                        return false;
                                    return true;
                                }, onlyRenderProperties: true }), errors.length ? (_jsxs(Box, Object.assign({ display: "inline-block" }, { children: [headerText, _jsx(Badge, Object.assign({ variant: "solid", colorScheme: "red", borderRadius: "full", ml: "1", verticalAlign: "top" }, { children: errors.length }))] }))) : (headerText)] })));
                };
                const extra = (_jsxs(ArrayBase.Item, Object.assign({ index: index, record: item, size: size }, { children: [_jsx(RecursionField, { schema: items, name: index, filterProperties: (schema) => {
                                if (!isOperationComponent(schema))
                                    return false;
                                return true;
                            }, onlyRenderProperties: true }), itemProps === null || itemProps === void 0 ? void 0 : itemProps.extra] })));
                const content = (_jsx(RecursionField, { schema: items, name: index, filterProperties: (schema) => {
                        if (isIndexComponent(schema))
                            return false;
                        if (isOperationComponent(schema))
                            return false;
                        return true;
                    } }));
                return (_createElement(AccordionItem, Object.assign({}, extraItemProps, { key: index }),
                    _jsxs(AccordionButton, Object.assign({ as: "div" }, { children: [_jsx(Box, Object.assign({ flex: "1", textAlign: "left", sx: {
                                    '> div:first-of-type': {
                                        display: 'inline-block',
                                        marginRight: '8px',
                                    },
                                } }, { children: renderHeader() })), _jsx(Box, { children: extra })] })),
                    _jsx(AccordionPanel, Object.assign({ pb: 6 }, panelStyle, { children: _jsx(ArrayBase.Item, Object.assign({ index: index, record: item }, { children: content }), index) }))));
            }) })));
    };
    return (_jsxs(ArrayBase, Object.assign({ onAdd: (index) => {
            setActiveKeys(insertActiveKeys(activeKeys, index));
        } }, { children: [renderEmpty(), renderItems(), renderAddition()] })));
});
const Panel = ({ children }) => {
    return _jsx(Fragment, { children: children });
};
Panel.displayName = 'AccordionPanel';
ArrayAccordion.defaultProps = defaultProps;
ArrayAccordion.displayName = 'ArrayAccordion';
ArrayAccordion.Panel = Panel;
ArrayBase.mixin(ArrayAccordion);
export default ArrayAccordion;
